import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "filter-box" }
const _hoisted_2 = { class: "button-row" }

import {
  DashboardDropdownFilterBuilding,
  DashboardDropdownFilterComplex,
  DashboardDropdownFilterProjectType
} from './filters'
import { ComplexProperties, BuildingProperties, ComplexFeature } from '../../types'

import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { InformationResponse } from '@/api-client/models/utilization/Contracts'
import { FeatureType, useFilters, useCurrentFeatureFilters } from '@/composables/useFilters'
import {
  EXTERNAL_PROJECT_STATUSES,
  EXTERNAL_PROJECT_TYPES,
  PROJECT_STATUSES,
  PROJECT_TYPES
} from '@/api-client/models/project/Contracts'
import { PROJECT_STATUS, PROJECT_TYPE } from '@/api-client/models/project/Constants'
import MinMaxSliderFilter from '@/design-system/molecules/MinMaxSliderFilter.vue'

type Props = {
  information: InformationResponse | undefined
  isCollapsed: boolean
}
type Emits = {
  (e: 'resetForm'): void
  (e: 'submitForm'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectAnalysisFilters',
  props: {
    information: {},
    isCollapsed: { type: Boolean }
  },
  emits: ["resetForm", "submitForm"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emits = __emit
const vuex = useStore()

const currentFeature = useCurrentFeatureFilters()
const filterType = computed(
  (): FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal => {
    if (!currentFeature.value) {
      return FeatureType.ProjectAnalysisExternal
    }
    return currentFeature.value.submitted.type as
      | FeatureType.ProjectAnalysisExternal
      | FeatureType.ProjectAnalysisInternal
  }
)
const filters = useFilters<
  FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
>(filterType.value)
const { t, locale } = useI18n()

const htmlRef = ref<HTMLElement | null>(null)
const complexes = computed<ComplexProperties[]>(() =>
  vuex.getters.allComplexes.map((x: ComplexFeature) => x.properties)
)
const buildings = computed<BuildingProperties[]>(() =>
  vuex.getters.allBuildings.map((x: ComplexFeature) => x.properties)
)
const filteredBuildings = computed<BuildingProperties[]>(() => {
  if (!filters.current.complex) {
    return buildings.value
  }
  const id = filters.current.complex.belongsToId
  return buildings.value.filter((x) => x.complexId === id)
})
// Need to change to project
const filteredProject = computed(() => {
  if (filters.current.type === FeatureType.ProjectAnalysisInternal) {
    return PROJECT_TYPES.map((x) => ({
      id: x,
      title: PROJECT_TYPE[x][locale.value]
    }))
  }
  return EXTERNAL_PROJECT_TYPES.map((x) => ({
    id: x,
    title: PROJECT_TYPE[x][locale.value]
  }))
})

const filteredStatuses = computed(() => {
  if (filters.current.type === FeatureType.ProjectAnalysisInternal) {
    return PROJECT_STATUSES.map((x) => ({
      id: x,
      title: PROJECT_STATUS[x][locale.value]
    }))
  }
  return EXTERNAL_PROJECT_STATUSES.map((x) => ({
    id: x,
    title: PROJECT_STATUS[x][locale.value]
  }))
})

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
)
watch(
  () => filters.current.buildings,
  (values) => {
    if (!filters.current.complex && values.length === 1) {
      updateComplexSelection(values)
    }
  }
)

function resetForm() {
  emits('resetForm')
}

function complexSelected(complex: ComplexProperties | undefined) {
  filters.$patch({
    current: {
      complex: complex,
      buildings: [],
      projectTypes: [],
      projectStatuses: []
    }
  })
}

function updateComplexSelection(buildings: BuildingProperties[]) {
  const complex = complexes.value.find((x) => x.belongsToId === buildings[0].complexId)
  filters.$patch({ current: { complex: complex } })
}

function updateBudget(value: { from: number; to: number }) {
  filters.$patch({ current: { budget: value } })
}

return (_ctx: any,_cache: any) => {
  return (!_ctx.isCollapsed)
    ? (_openBlock(), _createBlock(_Transition, {
        key: 0,
        name: "slide"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", {
              ref_key: "htmlRef",
              ref: htmlRef,
              class: "filter-container"
            }, [
              _createVNode(_unref(DashboardDropdownFilterComplex), {
                complexes: complexes.value,
                selectedComplex: _unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: complexSelected
              }, null, 8, ["complexes", "selectedComplex"]),
              _createVNode(_unref(DashboardDropdownFilterBuilding), {
                buildings: filteredBuildings.value,
                selectedBuildings: _unref(filters).current.buildings,
                presentAsMultiSelect: !!_unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: _cache[0] || (_cache[0] = (buildings) => _unref(filters).$patch({ current: { buildings } }))
              }, null, 8, ["buildings", "selectedBuildings", "presentAsMultiSelect"]),
              _createVNode(_unref(DashboardDropdownFilterProjectType), {
                type: "type",
                projectTypes: filteredProject.value,
                selectedProjectTypes: _unref(filters).current.projectTypes,
                presentAsMultiSelect: !!_unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: _cache[1] || (_cache[1] = (projectTypes) => _unref(filters).$patch({ current: { projectTypes } }))
              }, null, 8, ["projectTypes", "selectedProjectTypes", "presentAsMultiSelect"]),
              _createVNode(_unref(DashboardDropdownFilterProjectType), {
                type: "status",
                projectTypes: filteredStatuses.value,
                selectedProjectTypes: _unref(filters).current.projectStatuses,
                presentAsMultiSelect: !!_unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: _cache[2] || (_cache[2] = 
              (projectStatuses) => _unref(filters).$patch({ current: { projectStatuses } })
            )
              }, null, 8, ["projectTypes", "selectedProjectTypes", "presentAsMultiSelect"]),
              (_unref(filters).current.type === _unref(FeatureType).ProjectAnalysisInternal)
                ? (_openBlock(), _createBlock(MinMaxSliderFilter, {
                    key: 0,
                    from: _unref(filters).current.budget?.from ?? 0,
                    to: _unref(filters).current.budget?.to ?? 5000,
                    min: 0,
                    max: 5000,
                    step: 1,
                    format: { format: { type: 'format-string', format: '%s tkr' } },
                    fromColor: "#e0e0e0",
                    toColor: "#e0e0e0",
                    middleColor: "#3f46d6",
                    label: "Budget (tkr)",
                    labelType: "paragraph",
                    onUpdateValue: updateBudget
                  }, null, 8, ["from", "to"]))
                : _createCommentVNode("", true)
            ], 512),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(ButtonOutline, {
                size: "small",
                buttonText: _unref(t)('general__text__dashboard_reset_selections'),
                onOnClicked: resetForm
              }, null, 8, ["buttonText"]),
              _createVNode(ButtonPrimary, {
                size: "small",
                disabled: _unref(filters).isCurrentFilterSubmitted,
                buttonText: _unref(t)('general__text__dashboard_update_selections'),
                onOnClicked: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('submitForm')))
              }, null, 8, ["disabled", "buttonText"])
            ])
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})